import { z } from "zod";
import { AccountId } from "./ids.js";
import { KindReason, MessageKind } from "./kinds.js";
import { GhGroup, GhReason, KnownApp, MessageSource, ThreadSource } from "./sources.js";
/**
 * Firestore document shapes (architecture §8). Converters in `@kwp/firestore` parse every read
 * through these schemas, so a missing field is either a zod `.default()` here or an error.
 * Timestamps are `Date` on this side of the converter.
 */
export const Uid = z.string().min(1);
export const WorkspaceRole = z.enum(["owner", "member"]);
export const UserDoc = z.object({
    email: z.string().email(),
    name: z.string(),
    workspaceIds: z.array(z.string()).default([]),
    /**
     * New mail's From per workspace, for this person only (architecture §1, §8): workspace id → one
     * of their own accounts. Per member because members cannot send from each other's accounts.
     */
    defaultFrom: z.record(z.string(), AccountId).default({}),
    /**
     * Apps whose mail this person keeps out of All mail (architecture §6.7, WP-066). Their own view
     * (GitHub) and Notifications still list it; written only by the api (`people.setHiddenInAllMail`).
     */
    hiddenInAllMail: z.array(KnownApp).default([]),
    createdAt: z.date(),
});
export const WorkspaceDoc = z.object({
    name: z.string().min(1).max(60),
    members: z.record(Uid, WorkspaceRole),
    settings: z.object({
        aiEnabled: z.boolean().default(true),
    }),
    createdAt: z.date(),
});
export const Provider = z.enum(["gmail", "graph"]);
export const AccountStatus = z.enum(["connecting", "syncing", "ready", "needs_reconnect", "error"]);
/**
 * Where incremental sync resumes. Gmail (and the fake) keep one history id; Graph keeps one
 * delta link per synced folder (§6.3).
 */
export const SyncCursor = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("history"), historyId: z.string().min(1) }),
    z.object({ kind: z.literal("delta"), folders: z.record(z.string(), z.string()) }),
]);
/**
 * A full sync in progress (architecture §6.3.2), kept on the account so any later sync job can
 * carry on where the last one stopped. The worker writes it after every committed step and the
 * commit that advances the cursor clears it.
 */
export const FullSyncState = z.object({
    /** Stamped on every message this full sync writes (`fullSyncGen`); the sweep deletes the rest. */
    generation: z.string().min(1),
    startedAt: z.date(),
    /** The provider's token to resume listing after the last committed batch; null before the first. */
    resume: z.string().nullable(),
    /** Set once listing is complete: the cursor the final commit will store. Only the sweep is left. */
    cursor: SyncCursor.nullable(),
    /** The sweep's position: the last cached message id it has looked at. */
    sweepAfter: z.string().nullable(),
    batches: z.number().int().nonnegative(),
    messages: z.number().int().nonnegative(),
    swept: z.number().int().nonnegative(),
});
/** A colour as `#rrggbb`, lower case. */
export const HexColor = z.string().regex(/^#[0-9a-f]{6}$/);
/**
 * The raw fill's version (§6.7): raised when `classify` learns something mail cached earlier
 * should get, so every account's cached mail is classified again once. 2: sources (WP-062).
 */
export const RAW_FILL_VERSION = 2;
export const AccountDoc = z.object({
    ownerUid: Uid,
    workspaceId: z.string(),
    provider: Provider,
    address: z.string().email(),
    displayName: z.string().nullable().default(null),
    /** The mailbox's colour in the inbox (`#rrggbb`), chosen in Settings → Accounts; null: automatic. */
    color: HexColor.nullable().default(null),
    status: AccountStatus,
    statusDetail: z.string().nullable().default(null),
    cursors: z.object({
        gmailHistoryId: z.string().nullable().default(null),
        graphDelta: z.record(z.string(), z.string()).default({}),
    }),
    watch: z.object({
        expiresAt: z.date().nullable().default(null),
        subscriptionId: z.string().nullable().default(null),
    }),
    lastSyncedAt: z.date().nullable().default(null),
    /** When the owner was told this account needs reconnecting (sync only); null: not yet, or since reconnected. */
    reconnectNotifiedAt: z.date().nullable().default(null),
    /** A full sync that has not finished yet (sync only); null otherwise. */
    fullSync: FullSyncState.nullable().default(null),
    /** When the sync worker filled in `sentAt` on threads cached before it existed (WP-055); null: not yet. */
    sentFilledAt: z.date().nullable().default(null),
    /**
     * When every cached message had its headers captured and was classified (WP-061): set by a
     * full sync's final commit, or by the one-time raw fill (§6.7). Null: not yet.
     */
    rawFilledAt: z.date().nullable().default(null),
    /**
     * Which classification the raw fill last brought the cached mail to (`RAW_FILL_VERSION`); an
     * account filled at an older one is filled again. Accounts filled before it existed read as 1.
     */
    rawFillVersion: z.number().int().positive().default(1),
    /** The raw fill in progress (sync only): the provider's listing token, null before the first batch. */
    rawFill: z.object({ resume: z.string().nullable(), batches: z.number().int().nonnegative(), messages: z.number().int().nonnegative() }).nullable().default(null),
    /** When the correspondents were filled in from the cached sent mail (WP-061); null: not yet. */
    correspondentsFilledAt: z.date().nullable().default(null),
    /** When `fromAddress`/`fromDomain` were filled in on the cached mail (WP-063); null: not yet. */
    sendersFilledAt: z.date().nullable().default(null),
    createdAt: z.date(),
});
export const MailLocation = z.enum(["inbox", "archive", "spam", "trash", "sent", "drafts", "folder"]);
export const Address = z.object({ name: z.string().nullable(), email: z.string() });
/**
 * `accounts/{a}/threads/{t}`. No `workspaceId`: a thread's workspace is its account's, resolved at
 * query time (§8), so moving an account rewrites nothing. Older documents still hold one; it is
 * ignored.
 */
export const ThreadDoc = z.object({
    ownerUid: Uid,
    accountId: AccountId,
    subject: z.string(),
    snippet: z.string(),
    participants: z.array(Address),
    lastMessageAt: z.date(),
    location: MailLocation,
    folderName: z.string().nullable().default(null),
    tags: z.array(z.string()).default([]),
    unread: z.boolean(),
    starred: z.boolean(),
    hasAttachments: z.boolean(),
    messageIds: z.array(z.string()),
    /** Sender of the newest message that is not a draft; null for threads cached before it existed. */
    lastFrom: Address.nullable().default(null),
    /** The newest sent message's date, for the Sent view; null: none (or not filled in yet, §8). */
    sentAt: z.date().nullable().default(null),
    /** Set while the thread is snoozed (`mail.snooze`); the sync worker keeps it on re-folds. */
    snoozedUntil: z.date().nullable().default(null),
    /** The thread's kind (§6.7, `foldThread`); threads cached before WP-061 read as `person`. */
    kind: MessageKind.default("person"),
    kindReason: KindReason.default("default"),
    /** WP-062: what the thread is about when it comes from a known app, its GitHub reasons and group. */
    source: ThreadSource.nullable().default(null),
    ghReasons: z.array(GhReason).default([]),
    ghGroup: GhGroup.nullable().default(null),
    /** A person's only by default: no correspondent, no override (§6.7, WP-063); the row says "New". */
    newSender: z.boolean().default(false),
});
export const MessageDoc = z.object({
    ownerUid: Uid,
    threadId: z.string(),
    from: Address,
    to: z.array(Address),
    cc: z.array(Address).default([]),
    subject: z.string(),
    snippet: z.string(),
    date: z.date(),
    location: MailLocation,
    folderName: z.string().nullable().default(null),
    tags: z.array(z.string()).default([]),
    unread: z.boolean(),
    starred: z.boolean(),
    attachments: z.array(z.object({ name: z.string(), size: z.number(), mimeType: z.string() })).default([]),
    /** RFC 5322 Message-ID, for threading replies; null when the provider did not return it. */
    internetMessageId: z.string().nullable().default(null),
    hasAttachments: z.boolean().default(false),
    /** What `classify` decided at sync (§6.7); messages cached before WP-061 read as `person`. */
    kind: MessageKind.default("person"),
    kindReason: KindReason.default("default"),
    source: MessageSource.nullable().default(null),
    /**
     * The From address and its domain, lower case, so a sender's mail can be found (WP-063); null on
     * mail cached before, until the account's one-time fill (`sendersFilledAt`).
     */
    fromAddress: z.string().nullable().default(null),
    fromDomain: z.string().nullable().default(null),
    /** Its thread's GitHub group (WP-062), so `gh:` searches find every message of a thread in it. */
    ghGroup: GhGroup.nullable().default(null),
});
/**
 * `accounts/{a}/tags/{tagId}`: a tag the sync worker has seen on the account's mail (Gmail user
 * label, Graph category, or Graph user folder). The id is `tagDocId(name)` from `@kwp/firestore`.
 */
export const TagDoc = z.object({
    ownerUid: Uid,
    accountId: AccountId,
    name: z.string().min(1),
    kind: z.enum(["tag", "folder"]),
    color: z.string().nullable().default(null),
    updatedAt: z.date(),
});
export const TaskStatus = z.enum(["open", "done", "cancelled"]);
export const TaskVisibility = z.enum(["private", "workspace"]);
export const TaskSource = z.object({
    kind: z.enum(["email", "chat", "meeting", "cli"]),
    ref: z.string(),
});
export const TaskDoc = z.object({
    title: z.string().min(1).max(300),
    notes: z.string().max(10_000).default(""),
    status: TaskStatus,
    due: z.date().nullable().default(null),
    creatorUid: Uid,
    assigneeUid: Uid.nullable().default(null),
    visibility: TaskVisibility,
    source: TaskSource.nullable().default(null),
    external: z.object({ system: z.literal("kitwork"), id: z.string() }).nullable().default(null),
    createdAt: z.date(),
    updatedAt: z.date(),
    doneAt: z.date().nullable().default(null),
});
/** A push registration (architecture §11): written by the signed-in client, read by the api. */
export const DevicePlatform = z.enum(["web", "ios-pwa"]);
export const DeviceDoc = z.object({
    token: z.string().min(1).max(4095),
    platform: DevicePlatform,
    updatedAt: z.date(),
});
export const ChannelDoc = z.object({
    name: z.string().min(1).max(80),
    kind: z.enum(["channel", "dm"]),
    memberUids: z.array(Uid).default([]),
    /** Sorted member uids joined by "|", so a DM between the same people is found, not duplicated. */
    dmKey: z.string().nullable().default(null),
    lastMessageAt: z.date().nullable().default(null),
    createdAt: z.date(),
});
export const ChatRef = z.object({ kind: z.enum(["email", "task", "meeting"]), id: z.string(), label: z.string() });
/**
 * Images shared in chat (WP-058, architecture §11): the types the api accepts, judged by the
 * file's first bytes, never by its name or the browser's word. SVG is not an image here (it can
 * carry script).
 */
export const ChatImageType = z.enum(["image/png", "image/jpeg", "image/gif", "image/webp"]);
export const CHAT_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const CHAT_IMAGES_PER_MESSAGE = 4;
export const ChatImageId = z.string().regex(/^img_[A-Za-z0-9_-]{8,64}$/);
/** One image on a message. The bytes live in Cloud Storage; width and height are for layout. */
export const ChatImage = z.object({
    id: ChatImageId,
    mimeType: ChatImageType,
    size: z.number().int().positive().max(CHAT_IMAGE_MAX_BYTES),
    width: z.number().int().positive().max(20_000),
    height: z.number().int().positive().max(20_000),
});
export const ChatMessageDoc = z.object({
    authorUid: Uid,
    body: z.string().max(10_000),
    mentions: z.array(Uid).default([]),
    refs: z.array(ChatRef).default([]),
    images: z.array(ChatImage).max(CHAT_IMAGES_PER_MESSAGE).default([]),
    /** Set when the message records something the system did (a task created by /task). */
    kind: z.enum(["user", "system"]).default("user"),
    createdAt: z.date(),
    editedAt: z.date().nullable().default(null),
});
export const LinkDoc = z.object({
    title: z.string().min(1).max(120),
    url: z.string().url(),
    group: z.string().max(60).default("Links"),
    order: z.number().default(0),
});
/**
 * The inbox's quick filters (architecture §1). One at a time: each is one more equality (or
 * array-contains) on the unified inbox query, with its own composite index. `snoozed` is a view
 * rather than a filter: snoozed threads are archived, so it replaces `location == inbox` with
 * `snoozedUntil` set.
 */
export const InboxFilter = z.enum(["unread", "starred", "attachment", "tag", "snoozed", "sent"]);
/**
 * `users/{uid}/savedScopes/{id}` (§8): a named inbox scope. Empty `workspaceIds` and `accountIds`
 * mean every account; `accountIds` wins over `workspaceIds` when both are set.
 */
export const SavedScopeDoc = z.object({
    name: z.string().min(1).max(60),
    scope: z.object({
        workspaceIds: z.array(z.string().min(1)).max(10).default([]),
        accountIds: z.array(AccountId).max(10).default([]),
    }),
    filters: z
        .object({
        filter: InboxFilter.nullable().default(null),
        tag: z.string().min(1).max(100).nullable().default(null),
    })
        .default({ filter: null, tag: null }),
    createdAt: z.date(),
});
export const SavedScopeInput = SavedScopeDoc.omit({ createdAt: true });
/**
 * AI meeting notes (architecture §11, WP-017): `none` (not collected: Meet notes off, scope not
 * granted, or the watch could not start), `pending` (waiting for a transcript or summarising),
 * `ready`, `failed` (the model's output was unusable twice), `ai_disabled` (the workspace has AI
 * off: nothing is fetched or kept), `unavailable` (the meeting ended without a transcript).
 */
export const MeetingNotesStatus = z.enum(["none", "pending", "ready", "failed", "ai_disabled", "unavailable"]);
/** One action item from a meeting: who (a speaker's display name, as said) and by when (YYYY-MM-DD). */
export const MeetingActionItem = z.object({
    text: z.string().min(1).max(500),
    ownerName: z.string().min(1).max(120).nullable().default(null),
    due: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .default(null),
});
/**
 * `workspaces/{ws}/meetings/{id}` (architecture §11): a Meet started now (`spaceName`) or a
 * Calendar event with a Meet link (`eventId`). `startsAt` is when it starts (the creation time for
 * "start now"), which the dashboard and `meet.list` query by.
 */
export const MeetingDoc = z.object({
    meetUri: z.string().url(),
    spaceName: z.string().nullable().default(null),
    eventId: z.string().nullable().default(null),
    title: z.string(),
    kind: z.enum(["instant", "scheduled"]).default("instant"),
    /** The Google account (owned by `createdBy`) the meeting was created with. */
    accountId: z.string().nullable().default(null),
    startsAt: z.date(),
    endsAt: z.date().nullable().default(null),
    attendees: z.array(z.string()).default([]),
    /** The Calendar event's own page, for scheduled meetings. */
    calendarUrl: z.string().url().nullable().default(null),
    postedTo: z.object({ channelId: z.string(), messageId: z.string() }).nullable().default(null),
    createdBy: Uid,
    createdAt: z.date(),
    /** The Meet conference the notes were made from (`conferenceRecords/{id}`), once known. */
    conferenceRecord: z.string().nullable().default(null),
    notes: z
        .object({
        status: MeetingNotesStatus,
        summary: z.string().nullable().default(null),
        decisions: z.array(z.string()).default([]),
        actionItems: z.array(MeetingActionItem).default([]),
        /** The Claude model that wrote the summary. */
        model: z.string().nullable().default(null),
        generatedAt: z.date().nullable().default(null),
        /** Why notes are missing or failed, as a short reason (`subscribe_failed`, `invalid_model_output`, …). */
        detail: z.string().nullable().default(null),
    })
        .default({ status: "none", summary: null, decisions: [], actionItems: [], model: null, generatedAt: null, detail: null }),
});
/**
 * `meetWatches/{meetingId}` (server only, architecture §11): how the sync worker follows a meeting
 * until its notes exist. `subscription` is the Workspace Events subscription on the meeting's Meet
 * space (null when creating it failed; the renew cron retries); `watchUntil` is when to give up
 * (two days after the meeting ends); `expiresAt` drives the TTL that deletes the record.
 */
export const MeetWatchDoc = z.object({
    workspaceId: z.string().min(1),
    meetingId: z.string().min(1),
    /** The creator's Google account, whose token reads the transcript. */
    accountId: AccountId,
    /** `spaces/{id}`. */
    spaceName: z.string().min(1),
    subscription: z.string().nullable().default(null),
    subscriptionExpiresAt: z.date().nullable().default(null),
    watchUntil: z.date(),
    done: z.boolean().default(false),
    /** A notes run in progress (lease), so duplicate events do not summarise twice. */
    runUntil: z.date().nullable().default(null),
    createdAt: z.date(),
    expiresAt: z.date(),
});
/** Who or what asked for an action (architecture §9). */
export const Via = z.enum(["web", "cli", "mcp", "rule", "ai"]);
export const Capability = z.enum(["read", "organize", "destructive", "send"]);
/** One changed object: its state before and after, so `undo` can put it back. */
export const AuditTarget = z.object({ id: z.string(), before: z.unknown(), after: z.unknown() });
export const AuditDoc = z.object({
    ownerUid: Uid,
    actor: z.object({ uid: Uid, via: Via }),
    requestId: z.string(),
    action: z.string(),
    summary: z.string(),
    /** Inline targets; empty when they were too many and live in `audit/{id}/targets/{n}` chunks. */
    targets: z.array(AuditTarget),
    targetCount: z.number().int().nonnegative().default(0),
    targetChunks: z.number().int().nonnegative().default(0),
    undoable: z.boolean(),
    undoOf: z.string().nullable().default(null),
    undoneBy: z.string().nullable().default(null),
    at: z.date(),
});
export const ApprovalStatus = z.enum(["pending", "approved", "rejected", "expired", "failed"]);
export const ApprovalDoc = z.object({
    ownerUid: Uid,
    action: z.string(),
    capability: Capability,
    payload: z.unknown(),
    summary: z.string(),
    requestedVia: Via,
    status: ApprovalStatus,
    result: z.unknown().default(null),
    error: z.string().nullable().default(null),
    createdAt: z.date(),
    expiresAt: z.date(),
    decidedAt: z.date().nullable().default(null),
    /** Set when the approval came from applying a bulk plan: what the person reviews (§9 "Plans"). */
    plan: z.object({ planId: z.string(), matched: z.number().int().nonnegative(), sample: z.array(z.string()) }).nullable().default(null),
});
/** A bulk change computed but not yet applied (architecture §9 "Plans"). */
export const PlanDoc = z.object({
    ownerUid: Uid,
    action: z.string(),
    payload: z.unknown(),
    summary: z.string(),
    matched: z.number().int().nonnegative(),
    sample: z.array(z.string()),
    inputHash: z.string(),
    createdAt: z.date(),
    expiresAt: z.date(),
    appliedAt: z.date().nullable().default(null),
    auditId: z.string().nullable().default(null),
    approvalId: z.string().nullable().default(null),
});
//# sourceMappingURL=docs.js.map